import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { CreditCard, Building2, QrCode, CheckCircle, Loader2, ArrowLeft } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const ShopCheckout = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [shop, setShop] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [manualProofUploaded, setManualProofUploaded] = useState(false);
  
  // Cart and customer info from query params
  const shopId = searchParams.get('shop_id');
  const [cartItems, setCartItems] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    if (shopId) {
      fetchShopAndPaymentMethods();
      // Load cart from localStorage for this shop
      const savedCart = localStorage.getItem(`shop_cart_${shopId}`);
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }
    }
  }, [shopId]);

  const fetchShopAndPaymentMethods = async () => {
    try {
      const [shopRes, methodsRes] = await Promise.all([
        api.get(`/shops/id/${shopId}`),
        api.get(`/shop-payments/shop/${shopId}/methods`)
      ]);
      setShop(shopRes.data);
      setPaymentMethods(methodsRes.data);
      if (methodsRes.data.length > 0) {
        setSelectedMethod(methodsRes.data[0].type);
      }
    } catch (error) {
      console.error('Failed to fetch shop data:', error);
      toast.error('Failed to load checkout');
    } finally {
      setLoading(false);
    }
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handlePayment = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error('Please fill in all customer details');
      return;
    }

    setProcessingPayment(true);

    try {
      const orderData = {
        shop_id: shopId,
        items: cartItems.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        shipping_address: customerInfo.address,
        payment_method: selectedMethod,
        frontend_url: window.location.origin
      };

      const response = await api.post('/shop-payments/create-order', orderData);
      const order = response.data;

      if (selectedMethod === 'razorpay') {
        await handleRazorpayPayment(order);
      } else if (selectedMethod === 'stripe') {
        await handleStripePayment(order);
      } else if (selectedMethod === 'manual') {
        // For manual payment, navigate to order page with payment details
        localStorage.removeItem(`shop_cart_${shopId}`);
        toast.success('Order created! Please complete payment using the details provided.');
        navigate(`/shop-order-manual/${order.order_id}?shop=${shopId}`);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Payment failed');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleRazorpayPayment = async (order) => {
    // Load Razorpay script dynamically
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      const options = {
        key: order.razorpay_key_id,
        amount: order.total * 100, // Razorpay expects amount in paise
        currency: 'INR',
        name: shop?.name || 'Shop Payment',
        description: `Order #${order.order_number}`,
        order_id: order.razorpay_order_id,
        handler: async function (response) {
          try {
            // Verify payment on backend
            await api.post('/shop-payments/verify-razorpay', {
              order_id: order.order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            localStorage.removeItem(`shop_cart_${shopId}`);
            toast.success('Payment successful!');
            navigate(`/order-success?order_id=${order.order_id}`);
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: customerInfo.name,
          email: customerInfo.email,
          contact: customerInfo.phone
        },
        theme: {
          color: '#F59E0B'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    };
  };

  const handleStripePayment = async (order) => {
    // Redirect to Stripe Checkout
    if (order.stripe_checkout_url) {
      localStorage.setItem('pending_stripe_order', JSON.stringify({
        order_id: order.order_id,
        shop_id: shopId
      }));
      window.location.href = order.stripe_checkout_url;
    } else {
      toast.error('Stripe checkout URL not available');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!shopId || cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Your cart is empty</h2>
          <button onClick={() => navigate(-1)} className="btn-primary">
            <ArrowLeft className="w-4 h-4 mr-2" /> Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="shop-checkout-page">
      <div className="max-w-5xl mx-auto">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Shop
        </button>
        
        <h1 className="text-3xl md:text-4xl font-bold mb-8">
          Checkout - {shop?.name}
        </h1>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Order Summary */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
            
            <div className="glass-card divide-y divide-white/10">
              {cartItems.map((item, idx) => (
                <div key={idx} className="p-4 flex justify-between items-center">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-bold">₹{(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
              <div className="p-4 flex justify-between items-center bg-primary/10">
                <span className="text-lg font-bold">Total</span>
                <span className="text-2xl font-bold text-primary">₹{getCartTotal().toFixed(2)}</span>
              </div>
            </div>

            {/* Customer Info */}
            <div className="glass-card p-6 space-y-4">
              <h3 className="font-semibold mb-4">Customer Details</h3>
              <input
                type="text"
                placeholder="Full Name *"
                value={customerInfo.name}
                onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                data-testid="customer-name"
              />
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="email"
                  placeholder="Email *"
                  value={customerInfo.email}
                  onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                  data-testid="customer-email"
                />
                <input
                  type="tel"
                  placeholder="Phone *"
                  value={customerInfo.phone}
                  onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                  data-testid="customer-phone"
                />
              </div>
              <textarea
                placeholder="Shipping Address (optional)"
                value={customerInfo.address}
                onChange={(e) => setCustomerInfo({...customerInfo, address: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                rows={2}
                data-testid="customer-address"
              />
            </div>
          </div>

          {/* Payment Methods */}
          <div>
            <h2 className="text-xl font-semibold mb-4">Payment Method</h2>
            
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <label
                  key={method.type}
                  className={`glass-card p-4 flex items-center gap-4 cursor-pointer transition-all border-2 ${
                    selectedMethod === method.type ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-white/5'
                  }`}
                  data-testid={`payment-method-${method.type}`}
                >
                  <input
                    type="radio"
                    name="payment"
                    value={method.type}
                    checked={selectedMethod === method.type}
                    onChange={(e) => setSelectedMethod(e.target.value)}
                    className="w-5 h-5 accent-primary"
                  />
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center text-xl">
                    {method.icon}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{method.name}</p>
                    <p className="text-sm text-muted-foreground">{method.description}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Manual Payment Details */}
            {selectedMethod === 'manual' && (
              <div className="glass-card p-6 mt-4 space-y-4">
                <h3 className="font-semibold">Payment Details</h3>
                {paymentMethods.find(m => m.type === 'manual')?.details && (
                  <>
                    {paymentMethods.find(m => m.type === 'manual').details.bank_name && (
                      <div className="p-4 bg-white/5 rounded-xl space-y-2">
                        <p className="text-sm text-muted-foreground">Bank Transfer</p>
                        <p><strong>Bank:</strong> {paymentMethods.find(m => m.type === 'manual').details.bank_name}</p>
                        <p><strong>Account:</strong> {paymentMethods.find(m => m.type === 'manual').details.account_number}</p>
                        <p><strong>IFSC:</strong> {paymentMethods.find(m => m.type === 'manual').details.ifsc_code}</p>
                        <p><strong>Name:</strong> {paymentMethods.find(m => m.type === 'manual').details.account_holder}</p>
                      </div>
                    )}
                    {paymentMethods.find(m => m.type === 'manual').details.upi_id && (
                      <div className="p-4 bg-white/5 rounded-xl">
                        <p className="text-sm text-muted-foreground mb-2">UPI ID</p>
                        <p className="font-mono text-lg text-primary">
                          {paymentMethods.find(m => m.type === 'manual').details.upi_id}
                        </p>
                      </div>
                    )}
                    {paymentMethods.find(m => m.type === 'manual').details.upi_qr_image && (
                      <div className="flex justify-center">
                        <div className="p-4 bg-white rounded-xl">
                          <img 
                            src={paymentMethods.find(m => m.type === 'manual').details.upi_qr_image} 
                            alt="UPI QR" 
                            className="w-48 h-48 object-contain"
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <button
              onClick={handlePayment}
              disabled={processingPayment || !selectedMethod}
              className="w-full btn-primary mt-6 flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="pay-now-button"
            >
              {processingPayment ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Pay ₹{getCartTotal().toFixed(2)}
                </>
              )}
            </button>

            <p className="text-xs text-center text-muted-foreground mt-4">
              By proceeding, you agree to the terms and conditions
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopCheckout;
